import React, { useState, useEffect, useCallback } from "react";
import {
    Alert,
    Row,
    Col,
} from 'reactstrap';
import Select from 'react-select';
import { GuestHazardAttachmentSection } from "./GuestHazardAttachmentSection";
import DebouncedSaveButton from '../../../components/Buttons/DebounceSaveButton';
import { useDispatch, useSelector } from 'react-redux';
import { getPublicHazardValues, addGuestHazardForm } from "../../../store/actions/Incident/hazard";
import { FiMapPin, FiCalendar, FiInfo, FiAlertOctagon, FiFileText, FiPhone, FiMail, FiUser, FiMap, FiFlag, FiZap } from 'react-icons/fi';
import {
    AppleCard,
    AppleCardHeader,
    AppleCardBody,
    AppleForm,
    AppleFormGroup,
    AppleLabel,
    AppleInput,
    AppleButton,
    SectionTitle,
    FormSection,
    AppleFormWithDropdowns,
    ErrorMessage,
    getSelectProps
} from "../../../components/AppleStyles";
import { showSuccessDialog, showWarningDialog } from "../../../utils/dialogs";

const initialFormState = {
    name: '',
    email: '',
    phone: '',
    country: null,
    site: '',
    location: '',
    detectedOn: new Date().toISOString().split('T')[0],
    reportType: null,
    hazardSource: null,
    actualHazard: null,
    details: '',
    immediateActions: '',
    attachments: []
};

const initialErrorState = {
    name: '',
    email: '',
    phone: '',
    country: '',
    site: '',
    location: '',
    detectedOn: '',
    reportType: '',
    hazardSource: '',
    actualHazard: '',
    details: '',
};

const initialTouchedState = {
    name: false,
    email: false,
    phone: false,
    country: false,
    site: false,
    location: false,
    detectedOn: false,
    reportType: false,
    hazardSource: false,
    actualHazard: false,
    details: false,
};

const GuestHazardForm = () => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState(initialErrorState);
    const [touched, setTouched] = useState(initialTouchedState);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [globalErrorMessage, setGlobalErrorMessage] = useState("");

    const publicHazardValues = useSelector(state => state.hazard.publicHazardValues);
    const [reportTypeOptions, setReportTypeOptions] = useState([]);
    const [hazardSourceOptions, setHazardSourceOptions] = useState([]);
    const [actualHazardOptions, setActualHazardOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);

    useEffect(() => {
        dispatch(getPublicHazardValues());
    }, [dispatch]);

    useEffect(() => {
        if (publicHazardValues) {
            setReportTypeOptions(publicHazardValues.reportTypes?.map(type => ({ value: type.id, label: type.name })) || []);
            setHazardSourceOptions(publicHazardValues.hazardSources?.map(source => ({ value: source.id, label: source.name })) || []);
            setActualHazardOptions(publicHazardValues.actualHazards?.map(hazard => ({ value: hazard.id, label: hazard.name })) || []);
            setCountryOptions(publicHazardValues.countries?.map(country => ({ value: country.id, label: country.name })) || []);
        }
    }, [publicHazardValues]);

    const validateField = useCallback((name, value) => {
        let error = '';
        switch (name) {
            case 'name':
                if (!value) error = 'Name is required';
                else if (value.length < 2) error = 'Name must be at least 2 characters';
                break;
            case 'email':
                if (!value) error = 'Email is required';
                else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) error = 'Invalid email format';
                break;
            case 'phone':
                if (value && !/^[0-9+\-()\s]*$/.test(value)) error = 'Invalid characters in phone number';
                break;
            case 'country':
                if (!value) error = 'Country is required';
                break;
            case 'site':
                if (!value) error = 'Site is required';
                break;
            case 'location':
                if (!value) error = 'Location is required';
                else if (value.length < 3) error = 'Please provide more specific location details';
                break;
            case 'detectedOn':
                if (!value) {
                    error = 'Detection date is required';
                } else {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    selectedDate.setHours(0, 0, 0, 0);
                    today.setHours(0, 0, 0, 0);
                    if (selectedDate > today) error = 'Date cannot be in the future';
                }
                break;
            case 'reportType':
                if (!value) error = 'Report type is required';
                break;
            case 'hazardSource':
                if (!value) error = 'Hazard source is required';
                break;
            case 'actualHazard':
                if (!value) error = 'Actual hazard is required';
                break;
            case 'details':
                if (!value) error = 'Hazard details are required';
                else if (value.length < 10) error = 'Please provide at least 10 characters';
                else if (value.length > 1000) error = 'Details cannot exceed 1000 characters';
                break;
            default:
                break;
        }
        setErrors(prevErrors => ({ ...prevErrors, [name]: error }));
        return error === ''; // Return true if valid, false if error
    }, []);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
        if (touched[name]) {
          validateField(name, value);
        }
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prevData => ({ ...prevData, [name]: selectedOption }));
        if (touched[name]) {
            validateField(name, selectedOption);
        }
    };

    const handleBlur = (event) => {
        const { name, value } = event.target;
        if (!touched[name]) {
            setTouched(prevTouched => ({ ...prevTouched, [name]: true }));
        }
        validateField(name, value);
    };
    
    const handleSelectBlur = (name) => {
        if (!touched[name]) {
            setTouched(prevTouched => ({ ...prevTouched, [name]: true }));
        }
        validateField(name, formData[name]); // Validate with current value on blur
    };

    const handleAttachmentChange = (newAttachments) => {
        setFormData(prevData => ({ ...prevData, attachments: newAttachments }));
    };

    const resetForm = () => {
        setFormData(initialFormState);
        setErrors(initialErrorState);
        setTouched(initialTouchedState);
        setGlobalErrorMessage("");
    };

    const autofillForm = () => {
        if (reportTypeOptions.length && hazardSourceOptions.length && actualHazardOptions.length && countryOptions.length) {
            const randomReportType = reportTypeOptions[Math.floor(Math.random() * reportTypeOptions.length)];
            const randomHazardSource = hazardSourceOptions[Math.floor(Math.random() * hazardSourceOptions.length)];
            const randomActualHazard = actualHazardOptions[Math.floor(Math.random() * actualHazardOptions.length)];
            const randomCountry = countryOptions[Math.floor(Math.random() * countryOptions.length)];
            
            const autoData = {
                ...initialFormState, // Start with defaults like attachments
                name: 'John Smith',
                email: 'john.smith@example.com',
                phone: '+1-555-123-4567',
                country: randomCountry, // Use the dropdown value object
                site: 'Main Office Building',
                location: 'Third Floor, East Wing, Near Elevators',
                detectedOn: new Date().toISOString().split('T')[0],
                reportType: randomReportType,
                hazardSource: randomHazardSource,
                actualHazard: randomActualHazard,
                details: 'I noticed a potential safety hazard where the floor has a significant wet area near the water cooler that could cause someone to slip and fall. There is no warning sign present.',
                immediateActions: 'I informed the facilities team and placed my notebook on the floor as a temporary marker to alert people walking by.',
                attachments: formData.attachments // Keep existing attachments
            };
            setFormData(autoData);
            // Optionally, validate autofilled fields and clear errors
            setErrors(initialErrorState);
            const newTouched = {};
            Object.keys(initialTouchedState).forEach(key => newTouched[key] = true);
            setTouched(newTouched);

        } else {
            setGlobalErrorMessage("Cannot autofill form: Options not loaded yet. Please try again in a moment.");
        }
    };

    const validateAllFields = () => {
        let isValid = true;
        const newErrors = { ...initialErrorState };
        for (const field in formData) {
            if (Object.hasOwnProperty.call(initialErrorState, field)) {
                const fieldError = validateField(field, formData[field]);
                if(!fieldError) isValid = false; // validateField returns true if valid
            }
        }
        // After validateField updates errors state individually, this ensures a consolidated check.
        // A more direct approach would be for validateField to return the error string directly
        // and then build the newErrors object here to set it once.
        // For now, relying on validateField side effect and then checking errors state:
        let finalValid = true;
        for(const key in errors) {
            if(errors[key] !== '') {
                finalValid = false;
                break;
            }
        }
        return finalValid;
    };


    const handleSubmitForm = async (event) => {
        if (event) {
            event.preventDefault();
        }
        setIsSubmitting(true);
        setGlobalErrorMessage("");

        // Mark all fields as touched to show errors
        const allTouched = {};
        Object.keys(initialTouchedState).forEach(key => allTouched[key] = true);
        setTouched(allTouched);

        // Validate all fields
        let formIsValid = true;
        
        // Validate each field and update errors state
        Object.keys(initialErrorState).forEach(key => {
            if (!validateField(key, formData[key])) {
                formIsValid = false;
            }
        });

        // Double-check we don't have any error messages
        for (const key in errors) {
            if (errors[key]) {
                formIsValid = false;
                break;
            }
        }

        if (!formIsValid) {
            setIsSubmitting(false);
            setGlobalErrorMessage("Please correct the errors in the form.");
            return;
        }

        try {
            const submissionData = {
                ...formData,
                country: formData.country ? parseInt(formData.country.value) : null,
                reportType: formData.reportType ? formData.reportType.value : null,
                hazardSource: formData.hazardSource ? formData.hazardSource.value : null,
                actualHazard: formData.actualHazard ? formData.actualHazard.value : null,
            };

            const apiFormData = new FormData();
            Object.keys(submissionData).forEach(key => {
                if (key !== 'attachments' && submissionData[key] !== null && submissionData[key] !== undefined) {
                    apiFormData.append(key, submissionData[key]);
                }
            });

            formData.attachments.forEach((attachment, index) => {
                if (attachment.file instanceof File) {
                    apiFormData.append(`attachments[${index}]`, attachment.file, attachment.filename);
                }
            });
            
            apiFormData.append('files', JSON.stringify(formData.attachments.map(attachment => ({
                fileName: attachment.filename,
                description: attachment.description || null,
                newFile: attachment.file instanceof File
            }))));
            
            const response = await dispatch(addGuestHazardForm(apiFormData));

            if (response && response.success) {
                setSubmitted(true);
                
                resetForm();
            } else {
                setGlobalErrorMessage(response?.message || "Failed to submit hazard. Please try again.");
                showWarningDialog(response?.message || "Failed to submit hazard. Please try again.");
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            const message = error.response?.data?.message || error.message || "An unexpected error occurred. Please try again.";
            setGlobalErrorMessage(message);
            showWarningDialog(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <AppleCard className="mt-3">
                <AppleCardBody className="text-center">
                    <div className="py-5">
                        <div className="mb-4" style={{ color: '#28a745', fontSize: '3rem' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-check-circle" viewBox="0 0 16 16">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                            </svg>
                        </div>
                        <h2 className="text-success mb-4">Thank You!</h2>
                        <p className="mb-4">Your hazard report has been submitted successfully.</p>
                        <AppleButton color="primary" onClick={() => { setSubmitted(false); resetForm(); }}>
                            Submit Another Report
                        </AppleButton>
                    </div>
                </AppleCardBody>
            </AppleCard>
        );
    }

    const inputIconStyle = { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '16px' };
    const isFormValid = () => {
        for (const key in errors) {
            if(errors[key]) return false;
        }
        // Also check if all required fields are filled, as errors might be empty initially if not touched
        if(!formData.name || !formData.email || !formData.country || !formData.site || !formData.location || !formData.detectedOn || !formData.reportType || !formData.hazardSource || !formData.actualHazard || !formData.details) return false;
        return true;
    };
    const hasBeenDirty = Object.values(touched).some(t => t === true);

    return (
        <AppleFormWithDropdowns>
            <AppleCard className="mt-3">
                <AppleCardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2>Report a Hazard</h2>
                        
                    </div>
                </AppleCardHeader>
                <AppleCardBody>
                    <AppleForm onSubmit={handleSubmitForm} noValidate>
                        {globalErrorMessage && <Alert color="danger" className="mb-4">{globalErrorMessage}</Alert>}
                        
                        <FormSection>
                            <SectionTitle><FiUser className="me-2" /> Contact Information</SectionTitle>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Name <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiUser style={inputIconStyle} />
                                            <AppleInput
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                style={{ paddingLeft: "45px" }}
                                                className={(touched.name && errors.name) ? 'is-invalid' : (touched.name && !errors.name) ? 'is-valid' : ''}
                                            />
                                            {touched.name && errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Email <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiMail style={inputIconStyle} />
                                            <AppleInput
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                style={{ paddingLeft: "45px" }}
                                                className={(touched.email && errors.email) ? 'is-invalid' : (touched.email && !errors.email) ? 'is-valid' : ''}
                                            />
                                            {touched.email && errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Phone Number</AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiPhone style={inputIconStyle} />
                                            <AppleInput
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                style={{ paddingLeft: "45px" }}
                                                className={(touched.phone && errors.phone) ? 'is-invalid' : (touched.phone && !errors.phone && formData.phone) ? 'is-valid' : ''}
                                            />
                                            {touched.phone && errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                        </FormSection>

                        <FormSection>
                            <SectionTitle><FiMapPin className="me-2" /> Location Information</SectionTitle>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Country <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            
                                            <Select
                                                name="country"
                                                value={formData.country}
                                                onChange={(option) => handleSelectChange("country", option)}
                                                onBlur={() => handleSelectBlur("country")}
                                                options={countryOptions}
                                                placeholder="Select Country"
                                                isSearchable
                                                {...getSelectProps(touched.country && errors.country ? { message: errors.country } : null)}
                                            />
                                            {touched.country && errors.country && <ErrorMessage>{errors.country}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Site <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiMap style={inputIconStyle} />
                                            <AppleInput
                                                type="text"
                                                name="site"
                                                value={formData.site}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                style={{ paddingLeft: "45px" }}
                                                className={(touched.site && errors.site) ? 'is-invalid' : (touched.site && !errors.site) ? 'is-valid' : ''}
                                            />
                                            {touched.site && errors.site && <ErrorMessage>{errors.site}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Specific Location <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiMapPin style={inputIconStyle} />
                                            <AppleInput
                                                type="text"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                style={{ paddingLeft: "45px" }}
                                                placeholder="Be as specific as possible (e.g., 'North stairwell, 3rd floor')"
                                                className={(touched.location && errors.location) ? 'is-invalid' : (touched.location && !errors.location) ? 'is-valid' : ''}
                                            />
                                            {touched.location && errors.location && <ErrorMessage>{errors.location}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                        </FormSection>

                        <FormSection>
                            <SectionTitle><FiAlertOctagon className="me-2" /> Hazard Details</SectionTitle>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Detected On <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiCalendar style={inputIconStyle} />
                                            <AppleInput
                                                type="date"
                                                name="detectedOn"
                                                value={formData.detectedOn}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                style={{ paddingLeft: "45px" }}
                                                className={(touched.detectedOn && errors.detectedOn) ? 'is-invalid' : (touched.detectedOn && !errors.detectedOn) ? 'is-valid' : ''}
                                            />
                                            {touched.detectedOn && errors.detectedOn && <ErrorMessage>{errors.detectedOn}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Report Type <span className="text-danger">*</span></AppleLabel>
                                        <Select
                                            name="reportType"
                                            value={formData.reportType}
                                            onChange={(option) => handleSelectChange("reportType", option)}
                                            onBlur={() => handleSelectBlur("reportType")}
                                            options={reportTypeOptions}
                                            placeholder="Select Report Type"
                                            isSearchable
                                            {...getSelectProps(touched.reportType && errors.reportType ? { message: errors.reportType } : null)}
                                        />
                                        {touched.reportType && errors.reportType && <ErrorMessage>{errors.reportType}</ErrorMessage>}
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Hazard Source <span className="text-danger">*</span></AppleLabel>
                                        <Select
                                            name="hazardSource"
                                            value={formData.hazardSource}
                                            onChange={(option) => handleSelectChange("hazardSource", option)}
                                            onBlur={() => handleSelectBlur("hazardSource")}
                                            options={hazardSourceOptions}
                                            placeholder="Select Hazard Source"
                                            isSearchable
                                            {...getSelectProps(touched.hazardSource && errors.hazardSource ? { message: errors.hazardSource } : null)}
                                        />
                                        {touched.hazardSource && errors.hazardSource && <ErrorMessage>{errors.hazardSource}</ErrorMessage>}
                                    </AppleFormGroup>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Actual Hazard <span className="text-danger">*</span></AppleLabel>
                                        <Select
                                            name="actualHazard"
                                            value={formData.actualHazard}
                                            onChange={(option) => handleSelectChange("actualHazard", option)}
                                            onBlur={() => handleSelectBlur("actualHazard")}
                                            options={actualHazardOptions}
                                            placeholder="Select Actual Hazard"
                                            isSearchable
                                            {...getSelectProps(touched.actualHazard && errors.actualHazard ? { message: errors.actualHazard } : null)}
                                        />
                                        {touched.actualHazard && errors.actualHazard && <ErrorMessage>{errors.actualHazard}</ErrorMessage>}
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Hazard Details <span className="text-danger">*</span></AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiInfo style={{ ...inputIconStyle, top: '15px', alignItems: 'flex-start' }} />
                                            <AppleInput
                                                type="textarea"
                                                name="details"
                                                value={formData.details}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                rows="4"
                                                style={{ paddingLeft: "45px" }}
                                                placeholder="Describe the hazard in detail - what did you observe?"
                                                className={(touched.details && errors.details) ? 'is-invalid' : (touched.details && !errors.details) ? 'is-valid' : ''}
                                            />
                                            {touched.details && errors.details && <ErrorMessage>{errors.details}</ErrorMessage>}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <AppleFormGroup>
                                        <AppleLabel>Immediate Actions Taken</AppleLabel>
                                        <div style={{ position: "relative" }}>
                                            <FiFileText style={{ ...inputIconStyle, top: '15px', alignItems: 'flex-start' }} />
                                            <AppleInput
                                                type="textarea"
                                                name="immediateActions"
                                                value={formData.immediateActions}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                rows="3"
                                                style={{ paddingLeft: "45px" }}
                                                placeholder="Describe any immediate actions that were taken to address the hazard"
                                                className={(touched.immediateActions && errors.immediateActions) ? 'is-invalid' : (touched.immediateActions && !errors.immediateActions && formData.immediateActions) ? 'is-valid' : ''}
                                            />
                                            {/* No error message for optional field unless specific validation added */}
                                        </div>
                                    </AppleFormGroup>
                                </Col>
                            </Row>
                        </FormSection>

                        <FormSection>
                            <SectionTitle><FiFileText className="me-2" /> Attachments</SectionTitle>
                            <p className="text-muted small">Photos or other evidence of the hazard (optional)</p>
                            <GuestHazardAttachmentSection
                                attachments={formData.attachments}
                                onAttachmentsChange={handleAttachmentChange}
                            />
                        </FormSection>

                        <div className="d-flex justify-content-between mt-4">
                            <AppleButton color="secondary" type="button" onClick={() => { resetForm(); if (window.history.length > 1) window.history.back(); else console.log("Cannot go back further"); }}>
                                Cancel
                            </AppleButton>
                            <DebouncedSaveButton
                                onSave={handleSubmitForm} // Changed to direct call
                                isProcessing={isSubmitting}
                                text="Submit Report"
                                // Disable if submitting, or if form has been touched and is not valid
                                disabled={isSubmitting || (hasBeenDirty && !isFormValid())}
                            />
                        </div>
                    </AppleForm>
                </AppleCardBody>
            </AppleCard>
        </AppleFormWithDropdowns>
    );
};

export default GuestHazardForm; 